import React, { useCallback, useRef, useState } from 'react';
import { Menu, MenuItem } from '@material-ui/core';
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd';
import { BiDotsVerticalRounded, BiMenu, BiX } from 'react-icons/bi';
import { IoIosWarning } from 'react-icons/io';

import Toggle from '../Toggle';
import Button from '../Button';
import { ModalHandles } from '../Modal';
import { Product } from '../GridGroupAccordion';

import PlaceholderProductImage from '../../assets/no_image.png';

import {
  ActionButton,
  ProductDetails,
  TableCell,
  TableRow,
  Modal,
  ModalHeader,
  ProductImage,
} from './styles';
import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

type GridProductRowProps = {
  product: Product;
  isLoading: boolean;
  isExpanded: boolean;
  dragHandleProps?: DraggableProvidedDragHandleProps;
  onEdit(): void;
  onClone(): void;
  onDelete(): void;
  onEditImage(): void;
  onChangePauseSale(): void;
};

const GridProductRow: React.FC<GridProductRowProps> = ({
  product,
  isLoading,
  isExpanded,
  dragHandleProps,
  onEdit,
  onClone,
  onDelete,
  onEditImage,
  onChangePauseSale,
}) => {
  const modalRef = useRef<ModalHandles>(null);
  const optionsButtonRef = useRef<HTMLButtonElement>(null);
  const { findRotinas } = useAuth();
  const { addToast } = useToast();

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  function handleProductGrids(): string {
    if (product?.grids) {
      let suffix = 'grades';

      if (product.grids.length === 1) {
        suffix = suffix.slice(0, -1);
      }

      return `${product.grids.filter((grid) => grid.active).length} ${suffix}`;
    }

    return 'Nenhuma grade';
  }

  function handleProductPrice(): string {
    if (product?.minGridsPrice) {
      return Number(product.minGridsPrice).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      });
    }

    return 'Sem preço';
  }

  const handleOpenOptions = useCallback(() => {
    setIsOptionsOpen(true);
  }, []);

  const handleCloseOptions = useCallback(() => {
    setIsOptionsOpen(false);
  }, []);

  const handleAttemptToDeleteProduct = useCallback(() => {
    modalRef.current?.open();
    handleCloseOptions();
  }, [handleCloseOptions]);

  const handleEditProduct = useCallback(() => {
    handleCloseOptions();
    onEdit();
  }, [onEdit, handleCloseOptions]);

  const handleCloneProduct = useCallback(() => {
    handleCloseOptions();
    onClone();
  }, [onClone, handleCloseOptions]);

  const handleDeleteProduct = useCallback(() => {
    handleCloseOptions();
    onDelete();
  }, [onDelete, handleCloseOptions]);

  const hasPermission = useCallback(
    (): boolean => findRotinas('grupos')?.alterar || false,
    [findRotinas],
  );

  return (
    <TableRow>
      <TableCell>
        <ProductDetails>
          <button type="button" disabled={isLoading} {...dragHandleProps}>
            <BiMenu />
          </button>
          <ProductImage
            placeholder={PlaceholderProductImage}
            url={`${process.env.REACT_APP_SERVER_URL}/files/${product.image}`}
            onClick={onEditImage}
          >
            <div />
          </ProductImage>
          <p>{product.name}</p>
        </ProductDetails>
      </TableCell>
      <TableCell>
        <h4>Disponível em</h4>
        <p>{handleProductGrids()}</p>
      </TableCell>
      <TableCell>
        <h4>À partir de</h4>
        <p>{handleProductPrice()}</p>
      </TableCell>
      <TableCell>
        <Toggle
          checked={!product.pauseSale}
          firstText="Pausado"
          secondText="Ativado"
          onToggle={() => {
            if (!hasPermission()) {
              addToast({
                type: 'error',
                title: 'Não autorizado.',
                description: 'Você não tem permissão.',
              });
              return;
            }
            onChangePauseSale();
          }}
        />
      </TableCell>
      <TableCell>
        {hasPermission() && (
          <>
            <ActionButton
              isExpanded={isExpanded}
              ref={optionsButtonRef}
              type="button"
              onClick={handleOpenOptions}
            >
              <BiDotsVerticalRounded />
            </ActionButton>
            <Menu
              id={`grid-product-${product.id}-options-menu`}
              anchorEl={optionsButtonRef.current}
              keepMounted
              open={isOptionsOpen}
              onClose={handleCloseOptions}
              PaperProps={{
                style: {
                  maxHeight: 48 * 4.5,
                  width: '20ch',
                },
              }}
            >
              <MenuItem onClick={handleEditProduct}>Editar</MenuItem>
              <MenuItem onClick={handleCloneProduct}>Clonar</MenuItem>
              <MenuItem onClick={handleAttemptToDeleteProduct}>
                Excluir
              </MenuItem>
            </Menu>
          </>
        )}
      </TableCell>
      <Modal ref={modalRef} containerStyle={{ zIndex: 99999999 }}>
        <ModalHeader>
          <div>
            <IoIosWarning />
          </div>
          <h1>Atenção</h1>
          <button type="button" onClick={() => modalRef.current?.close()}>
            <BiX />
          </button>
        </ModalHeader>
        <p>
          Ao realizar a ação todos os dados do produto &quot;{product.name}
          &quot; e suas grades serão excluídas.
          <br />
          <strong>Deseja prosseguir?</strong>
        </p>
        <Button full onClick={handleDeleteProduct}>
          Confirmar
        </Button>
        <Button
          full
          variant="outlined"
          onClick={() => modalRef.current?.close()}
        >
          Cancelar
        </Button>
      </Modal>
    </TableRow>
  );
};

export default GridProductRow;
